import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import MyBoxLoader from 'components/MyBoxLoader';

function formatRelativeTime(dateInput) {
  if (!dateInput) return '';
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return String(dateInput);

  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHours = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function cleanTitle(title = '', category = '') {
  const raw = title.trim();
  const upper = raw.toUpperCase();

  if (upper === 'CREATE_ORDER') return 'Order Created';
  if (upper === 'AUTO_DISPATCH') return 'Order Assigned';
  if (upper === '2FA VERIFIED' || upper.includes('2FA')) return 'Security Verification';
  if (upper === 'ADMIN LOGGED IN') return 'Admin Sign In';
  if (upper.includes('HARDWARE_RESET') || upper.includes('RESTART')) return 'Hardware Restart';
  if (upper.includes('SET_VOLUME') || upper.includes('VOLUME')) return 'Device Volume Updated';

  // If screaming snake case, convert to Title Case
  if (/^[A-Z0-9_]+$/.test(raw)) {
    return raw
      .split('_')
      .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
      .join(' ');
  }

  return raw || (category ? category.charAt(0).toUpperCase() + category.slice(1) : 'System Event');
}

function cleanSource(actor, category) {
  if (!actor || actor === 'System' || actor === 'system') {
    if (category === 'order') return 'Order Management';
    if (category === 'hardware') return 'Hardware Service';
    if (category === 'security') return 'Security Service';
    return 'System';
  }
  // Strip redundant wrapper e.g. "System (Auto-Dispatch)" -> "Auto-Dispatch"
  const match = actor.match(/\((.*?)\)/);
  if (match && match[1]) {
    return match[1];
  }
  return actor;
}

export default function NotificationList({
  notifications = [],
  readIds = new Set(),
  loading = false,
  onMarkRead
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  if (loading && notifications.length === 0) {
    return (
      <Box sx={{ py: 4, px: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <MyBoxLoader size={46} />
      </Box>
    );
  }

  if (!loading && notifications.length === 0) {
    return (
      <Box sx={{ py: 6, px: 3, textAlign: 'center' }}>
        <Typography
          sx={{
            color: isDark ? '#F8FAFC' : '#334155',
            fontFamily: 'Poppins, sans-serif',
            fontSize: '13.5px',
            fontWeight: 500,
            mb: 0.5
          }}
        >
          No notifications
        </Typography>
        <Typography
          sx={{
            color: isDark ? '#64748B' : '#94A3B8',
            fontFamily: 'Poppins, sans-serif',
            fontSize: '12px'
          }}
        >
          Operational logs and locker activity will appear here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {notifications.map((item, idx) => {
        const isRead = readIds.has(item.id);
        const title = cleanTitle(item.title, item.category);
        const timeAgo = formatRelativeTime(item.createTime || item.timestampMs);
        const source = cleanSource(item.actor, item.category);

        // Build cohesive metadata line: e.g. "20 hours ago · Auto-Dispatch · Door #0"
        const metaParts = [];
        if (timeAgo) metaParts.push(timeAgo);
        if (source) metaParts.push(source);
        if (item.boxNo !== undefined && item.boxNo !== null) metaParts.push(`Door #${item.boxNo}`);
        if (item.deviceNo) metaParts.push(item.deviceNo);
        const metaString = metaParts.join(' · ');

        return (
          <Box
            key={item.id || idx}
            onClick={() => onMarkRead && onMarkRead(item.id)}
            sx={{
              px: 2,
              py: 1.6,
              borderBottom: `1px solid ${isDark ? (theme.palette?.border?.subtle || 'rgba(255, 255, 255, 0.05)') : '#F1F5F9'}`,
              bgcolor: isDark ? (theme.palette?.background?.raised || '#272B33') : '#FFFFFF',
              cursor: 'pointer',
              transition: 'background-color 0.12s ease',
              '&:hover': {
                bgcolor: isDark ? (theme.palette?.background?.hover || '#2A2E36') : '#F8FAFC'
              }
            }}
          >
            {/* Title & Unread Indicator */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
              <Typography
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '13.5px',
                  fontWeight: isRead ? 500 : 600,
                  color: isRead ? (isDark ? (theme.palette?.text?.secondary || '#A4ABB8') : '#475569') : (isDark ? (theme.palette?.text?.primary || '#ECEEF1') : '#0F172A'),
                  letterSpacing: '-0.01em',
                  lineHeight: 1.35
                }}
              >
                {title}
              </Typography>

              {!isRead && (
                <Box
                  sx={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    bgcolor: isDark ? '#2DD4BF' : '#0D9488',
                    flexShrink: 0
                  }}
                />
              )}
            </Box>

            {/* Description Body */}
            {item.message && (
              <Typography
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '12.5px',
                  fontWeight: 400,
                  color: isRead ? (isDark ? (theme.palette?.text?.placeholder || '#707886') : '#64748B') : (isDark ? (theme.palette?.text?.secondary || '#A4ABB8') : '#334155'),
                  lineHeight: 1.45,
                  mb: 0.75,
                  wordBreak: 'break-word'
                }}
              >
                {item.message}
              </Typography>
            )}

            {/* Metadata Line */}
            {metaString && (
              <Typography
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '11px',
                  fontWeight: 400,
                  color: isDark ? (theme.palette?.text?.placeholder || '#707886') : '#94A3B8',
                  lineHeight: 1.2
                }}
              >
                {metaString}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

NotificationList.propTypes = {
  notifications: PropTypes.array,
  readIds: PropTypes.object,
  loading: PropTypes.bool,
  onMarkRead: PropTypes.func
};
